import { delay } from "./delay";

type PromiseOrUndefined = Promise<unknown> | undefined;

/**
 * Returns true if `maybePromise` implements the `Promise` interface (.then and .catch)
 */
export const isPromise = (
  maybePromise: unknown
): maybePromise is Promise<unknown> =>
  typeof (maybePromise as PromiseOrUndefined)?.then === "function" &&
  typeof (maybePromise as PromiseOrUndefined)?.catch === "function";

export async function wrapAsyncFn<T>(
  asyncFunc: () => Promise<T>,
  config: {
    before?: () => void;
    after?: () => void;
  }
): Promise<T> {
  const { before, after } = config;
  try {
    before?.();
    return await asyncFunc();
  } finally {
    after?.();
  }
}

/**
 * Implements the FullJitter exponential backoff algorithm outlined here
 * https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/
 */
export async function retryPromise<T>(promise: () => Promise<T>): Promise<T> {
  for (let i = 0; i < 5; i++) {
    try {
      return await promise();
    } catch (err) {
      if (i === 4) {
        throw err;
      }
    }
    const sleep = Math.floor(Math.random() * 1000 * 2 ** i);
    await delay(sleep);
  }

  // Should never happen
  throw new Error("Failed to retry promise");
}
